import { useEffect } from "react";
import { useRouter } from "expo-router";
import { toast } from "sonner-native";

import { CoverageEventsFlowStepWrapper } from "@/blocks/CoverageEventsFlowStepWrapper";
import { WorkRequirementsTitle } from "@/blocks/WorkRequirementsTitle";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnText } from "@/components/VnText/VnText";
import { useUpdateWorkRequirementScreeningMutation } from "@/data/mutations/workRequirementsMutations";
import {
  getNextWorkRequirementStepLink,
  toScreeningMembers,
} from "@/domain/workRequirements";
import { useTranslator } from "@/hooks/useTranslate";
import { useWorkRequirementScreeningStep } from "@/hooks/useWorkRequirementScreeningStep";

import { WorkRequirementMembersNextSteps } from "./WorkRequirementMembersNextSteps";

export const WorkRequirementNextStepsScreen = () => {
  const router = useRouter();
  const { t } = useTranslator();
  const { workRequirementId, workRequirement, isLoading, goBack } =
    useWorkRequirementScreeningStep();
  const {
    mutate: updateScreening,
    isPending: isCompleting,
    isError: hasCompletionFailed,
  } = useUpdateWorkRequirementScreeningMutation();

  // Reaching this step completes the screening: the server then computes each
  // member's status and the proof they need, which this screen lists.
  const isCompleted =
    workRequirement?.screeningStatus === "COMPLETED" &&
    workRequirement.screeningLastStep === "NEXT_STEPS";

  useEffect(() => {
    if (
      !workRequirement ||
      isCompleted ||
      isCompleting ||
      hasCompletionFailed
    ) {
      return;
    }

    // Only complete when the saved progress leads here, so landing on this
    // screen out of order (browser history, a pasted URL) resumes the
    // screening instead of completing it with unanswered steps.
    const { screeningLastStep, members } = workRequirement;
    const nextStepLink = getNextWorkRequirementStepLink({
      workRequirementId,
      step: screeningLastStep,
      members,
    });
    const canComplete =
      nextStepLink.pathname === "/work-requirement/[id]/next-steps";

    if (!canComplete) {
      router.replace(nextStepLink);
      return;
    }

    updateScreening(
      {
        workRequirementId,
        screening: {
          lastStep: "NEXT_STEPS",
          isComplete: true,
          members: toScreeningMembers(workRequirement),
        },
      },
      {
        // Going back and returning to this step retries the completion.
        onError: () => {
          toast.error(t("common:errors.unknown"));
        },
      },
    );
  }, [
    t,
    workRequirement,
    workRequirementId,
    isCompleted,
    isCompleting,
    hasCompletionFailed,
    updateScreening,
    router,
  ]);

  if (isLoading || !workRequirement || !isCompleted) {
    return (
      <CoverageEventsFlowStepWrapper
        onBack={goBack}
        shouldShowNextButton={false}>
        <VnLoadingSpinner />
      </CoverageEventsFlowStepWrapper>
    );
  }

  return (
    <CoverageEventsFlowStepWrapper
      onBack={goBack}
      onNext={() =>
        router.navigate(
          getNextWorkRequirementStepLink({
            workRequirementId,
            step: "NEXT_STEPS",
            members: workRequirement.members,
          }),
        )
      }>
      <WorkRequirementsTitle
        rule={workRequirement.rule}
        i18nKey="native:workRequirements.nextSteps.title"
      />
      <VnText
        variant="lg"
        className="text-muted-foreground mb-4"
        i18nKey="native:workRequirements.nextSteps.description"
      />
      <WorkRequirementMembersNextSteps workRequirement={workRequirement} />
    </CoverageEventsFlowStepWrapper>
  );
};
